import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Loading from '../../../components/shared/Loading/Loading';
import Button from '../../../components/shared/Button/Button';
import Modal from '../../../components/shared/Modal/Modal';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import { useConfirm } from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import {
  FormGroup,
  FormLabel,
  FormTextarea,
  FormHint,
  FormActions,
  ErrorMessage,
} from '../../../components/shared/Form/Form.styles';
import { SalesContainer, PageHead, Panel, Notice } from '../../sales/Sales.styles';
import { EmptyBox } from '../../sales/SalesList.styles';
import PromoterSalesView from '../../../components/promoters/PromoterSalesView';
import PromoterCutsView from '../../../components/promoters/PromoterCutsView';
import {
  PanelTabs,
  ItemList,
  ItemCard,
  Chip,
  LinkButton,
} from '../../../components/promoters/promoters.styles';
import { kindLabel, linkStatus, endedByLabel } from '../../../components/promoters/promoterLabels';
import {
  getEmpresaPromoterCut,
  listEmpresaPromoterCuts,
  listEmpresaPromoterLinks,
  listEmpresaPromoterSales,
  respondPromoterLink,
} from '../../../services/empresaPromoters';
import { formatTimestamp } from '../../../utils/dates';

const NOTE_MAX = 500;

const TABS = [
  { id: 'solicitudes', label: 'Solicitudes' },
  { id: 'ventas', label: 'Ventas por promotor' },
  { id: 'cortes', label: 'Cortes semanales' },
];

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'accepted', label: 'Aceptadas' },
  { value: 'rejected', label: 'Rechazadas' },
  { value: 'revoked', label: 'Revocadas' },
  { value: 'all', label: 'Todas' },
];

// Modal de la marca con nota opcional para rechazar o revocar. La nota la ve el promotor.
const NoteModal = ({ link, action, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const revoke = action === 'revoke';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await respondPromoterLink(link.id, action, note.trim());
      onDone(data.link, data.message);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      // 400 "ya no está pendiente/aceptada": la lista se actualiza
      if (err.status === 400 || err.status === 404) onDone(null, null, true);
    }
  };

  return (
    <Modal
      title={revoke ? `¿Revocar a ${link.promoter.name}?` : `¿Rechazar a ${link.promoter.name}?`}
      onClose={busy ? null : onClose}
      maxWidth="500px"
      labelledBy="note-title"
    >
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
          {revoke ? (
            <>
              Desde ahora <strong>no se le atribuirán ventas nuevas</strong>. Las ventas que ya hizo siguen en sus cortes y
              debes pagárselas.
            </>
          ) : (
            <>No podrá vender tus actividades. Podría volver a solicitarlo más adelante.</>
          )}
        </p>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <FormGroup>
          <FormLabel htmlFor="link-note">Nota para el promotor (opcional)</FormLabel>
          <FormTextarea
            id="link-note"
            rows={3}
            maxLength={NOTE_MAX}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
          />
          <FormHint>El promotor la verá junto a su solicitud. {note.length}/{NOTE_MAX}</FormHint>
        </FormGroup>
        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="danger" disabled={busy}>
            {busy ? 'Guardando…' : revoke ? 'Revocar' : 'Rechazar'}
          </Button>
        </FormActions>
      </form>
    </Modal>
  );
};

const RequestsTab = ({ onChanged }) => {
  const [status, setStatus] = useState('pending');
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // { link, action }
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listEmpresaPromoterLinks(status);
      setLinks(data.links || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (link) => {
    const ok = await confirm({
      title: `¿Aceptar a ${link.promoter.name}?`,
      message: (
        <>
          <p>
            Podrá compartir el enlace de <strong>todas tus actividades activas</strong>. Las ventas hechas con su enlace le generan una
            comisión que tú le pagas. El porcentaje lo fija BokerHub y verás el monto en cada venta y corte.
          </p>
          <p>Puedes revocarlo cuando quieras; lo ya vendido sigue contando.</p>
        </>
      ),
      confirmLabel: 'Aceptar promotor',
    });
    if (!ok) return;
    setBusyId(link.id);
    setFlash('');
    setError('');
    try {
      const data = await respondPromoterLink(link.id, 'accept');
      setFlash(data.message || `Aceptaste a ${link.promoter.name}.`);
      load();
      onChanged();
    } catch (err) {
      setError(err.message);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const handleNoteDone = (link, message, failed) => {
    if (!failed) {
      setNoteFor(null);
      setFlash(message || 'Listo.');
      onChanged();
    }
    load();
  };

  return (
    <>
      {flash && <Notice role="status">{flash}</Notice>}

      <Panel>
        <h2>Solicitudes de promotores</h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 12 }}>
          Los promotores piden vender tus actividades. Tú aceptas, rechazas o revocas. BokerHub solo lleva el registro de lo vendido y de lo que les debes.
        </p>
        <div role="group" aria-label="Filtrar solicitudes" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="small"
              variant={status === f.value ? 'primary' : 'ghost'}
              aria-pressed={status === f.value}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </Panel>

      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={load}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {loading && !links ? (
        <Loading text="Cargando solicitudes..." />
      ) : links && links.length === 0 && !error ? (
        <EmptyBox>
          <h3>{status === 'pending' ? 'No tienes solicitudes pendientes' : 'No hay solicitudes con este filtro'}</h3>
          <p>Cuando un promotor pida vender tus actividades, aparecerá aquí.</p>
        </EmptyBox>
      ) : (
        <ItemList aria-busy={loading}>
          {(links || []).map((link) => {
            const state = linkStatus(link.status);
            const by = endedByLabel(link.endedBy);
            const p = link.promoter;
            return (
              <ItemCard key={link.id} data-muted={link.status === 'rejected' || link.status === 'revoked'}>
                <div className="head">
                  <span className="title">{p.name}</span>
                  <StatusBadge status={state.status}>{state.label}</StatusBadge>
                </div>
                <div className="meta">
                  <Chip>{kindLabel(p.kind)}</Chip>
                  {p.email && <> {p.email}</>}
                  {p.phone && <> · {p.phone}</>}
                </div>
                {p.approved === false && (
                  <div className="meta">BokerHub aún no aprueba a este promotor; todavía no puede operar.</div>
                )}
                <div className="meta">
                  Solicitada el {formatTimestamp(link.requestedAt)}
                  {link.respondedAt ? ` · respondida el ${formatTimestamp(link.respondedAt)}` : ''}
                  {link.revokedAt ? ` · terminada el ${formatTimestamp(link.revokedAt)}${by ? ` por ${by}` : ''}` : ''}
                </div>
                {link.message && <div className="note"><strong>Su mensaje:</strong> {link.message}</div>}
                {link.responseNote && <div className="note"><strong>Tu nota:</strong> {link.responseNote}</div>}
                <div className="actions">
                  {link.status === 'pending' && (
                    <>
                      <Button size="small" variant="success" disabled={busyId === link.id} onClick={() => handleAccept(link)}>Aceptar</Button>
                      <Button size="small" variant="danger" disabled={busyId === link.id} onClick={() => setNoteFor({ link, action: 'reject' })}>Rechazar</Button>
                    </>
                  )}
                  {link.status === 'accepted' && (
                    <Button size="small" variant="danger" disabled={busyId === link.id} onClick={() => setNoteFor({ link, action: 'revoke' })}>Revocar</Button>
                  )}
                </div>
              </ItemCard>
            );
          })}
        </ItemList>
      )}

      {noteFor && (
        <NoteModal
          link={noteFor.link}
          action={noteFor.action}
          onClose={() => setNoteFor(null)}
          onDone={handleNoteDone}
        />
      )}
      {dialog}
    </>
  );
};

// Pantalla de la empresa dueña: solicitudes de promotores, ventas por promotor y
// cortes semanales. El staff no entra aquí: no tiene sesión de empresa, y el
// backend responde 403 a su token.
const EmpresaPromoters = () => {
  const { empresa } = useEmpresaAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'solicitudes';
  const [allLinks, setAllLinks] = useState([]);
  const [linksVersion, setLinksVersion] = useState(0);

  useEffect(() => {
    if (!empresa) navigate('/empresa/login', { replace: true });
  }, [empresa, navigate]);

  // Promotores con relación (para los filtros de ventas y cortes).
  useEffect(() => {
    if (!empresa) return undefined;
    let cancelled = false;
    listEmpresaPromoterLinks('all')
      .then((data) => { if (!cancelled) setAllLinks(data.links || []); })
      .catch(() => { /* sin la lista el filtro por promotor simplemente no aparece */ });
    return () => { cancelled = true; };
  }, [empresa, linksVersion]);

  const promoterOptions = useMemo(() => {
    const seen = new Map();
    allLinks.forEach((l) => {
      if (l.status === 'accepted' || l.status === 'revoked') seen.set(l.promoter.id, l.promoter.name);
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [allLinks]);

  const loadSales = useCallback((p) => listEmpresaPromoterSales(p), []);
  const loadCuts = useCallback((p) => listEmpresaPromoterCuts(p), []);
  const loadCut = useCallback((id) => getEmpresaPromoterCut(id), []);

  if (!empresa) return <Loading text="Cargando..." />;

  return (
    <Layout headerType="empresa">
      <SalesContainer>
        <PageHead>
          <h1>Promotores</h1>
          <p>Quién vende tus actividades, cuánto vendió y cuánto le debes.</p>
        </PageHead>

        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 16 }}>
          ¿Quieres vender las actividades de otras empresas? <Link to="/empresa/promotor">Vender como promotor</Link>
        </p>

        <PanelTabs role="tablist" aria-label="Secciones de promotores">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`etab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls="etab-panel"
              onClick={() => setParams({ tab: t.id }, { replace: true })}
            >
              {t.label}
            </button>
          ))}
        </PanelTabs>

        <div id="etab-panel" role="tabpanel" aria-labelledby={`etab-${tab}`}>
          {tab === 'solicitudes' && <RequestsTab onChanged={() => setLinksVersion((v) => v + 1)} />}
          {tab === 'ventas' && (
            <PromoterSalesView viewer="empresa" load={loadSales} promoterOptions={promoterOptions} />
          )}
          {tab === 'cortes' && (
            <PromoterCutsView viewer="empresa" load={loadCuts} loadDetail={loadCut} promoterOptions={promoterOptions} />
          )}
        </div>
      </SalesContainer>
    </Layout>
  );
};

export default EmpresaPromoters;
